import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { apiFetch } from '../utils/api'
import { removeTokens } from '../utils/auth'
import { validatePasswordComplexity } from '../utils/validation'
import AddressFields from './AddressFields'

const UserProfile = ({ user, onBack }) => {
  const [activeTab, setActiveTab] = useState('general')
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    street: '',
    postalCode: '',
    city: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
    emailNotifications: true,
    slackNotifications: false,
    telegramNotifications: false
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        street: user.street || '',
        postalCode: user.postal_code || '',
        city: user.city || '',
        email: user.email || '',
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
        emailNotifications: user.email_notifications === undefined ? true : user.email_notifications,
        slackNotifications: user.slack_notifications === undefined ? false : user.slack_notifications,
        telegramNotifications: user.telegram_notifications === undefined ? false : user.telegram_notifications
      })
    }
  }, [user])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    if (error) setError('')
    if (success) setSuccess('')
  }

  const validatePasswordChange = () => {
    const { currentPassword, newPassword, confirmNewPassword } = formData

    if (!currentPassword && !newPassword && !confirmNewPassword) {
      return null
    }

    if (!currentPassword) {
      return 'Current password is required to change password'
    }

    if (!newPassword) {
      return 'New password is required'
    }

    const passwordError = validatePasswordComplexity(newPassword)
    if (passwordError) {
      return passwordError
    }

    if (newPassword !== confirmNewPassword) {
      return 'New passwords do not match'
    }

    return null
  }

  const prepareSubmitData = () => {
    const baseData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      street: formData.street,
      postalCode: formData.postalCode,
      city: formData.city
    }

    // Only include notification settings if they've been modified
    if (activeTab === 'notifications') {
      baseData.emailNotifications = formData.emailNotifications
      baseData.slackNotifications = formData.slackNotifications
      baseData.telegramNotifications = formData.telegramNotifications
    }

    const { currentPassword, newPassword, confirmNewPassword } = formData

    if (currentPassword || newPassword || confirmNewPassword) {
      return {
        ...baseData,
        currentPassword: currentPassword,
        newPassword: newPassword
      }
    }

    return baseData
  }

  const handleSubmitGeneral = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')
    setSuccess('')

    try {
      const response = await apiFetch('/api/profile', {
        method: 'PUT',
        body: JSON.stringify(prepareSubmitData())
      })

      const data = await response.json()

      if (response.ok) {
        setSuccess('Profile updated successfully')
      } else {
        setError(data.error || 'Failed to update profile')
      }
    } catch (err) {
      setError('Network error. Please check if the backend server is running.')
      console.error('Update profile error:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmitPassword = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')
    setSuccess('')

    const passwordValidationError = validatePasswordChange()
    if (passwordValidationError) {
      setError(passwordValidationError)
      setIsLoading(false)
      return
    }

    try {
      const response = await apiFetch('/api/profile', {
        method: 'PUT',
        body: JSON.stringify(prepareSubmitData())
      })

      const data = await response.json()

      if (response.ok) {
        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmNewPassword: ''
        }))
        // The old session is revoked. Browsers get the new one only as HttpOnly cookies
        // (session_renewed), never in the body; later requests use the cookies.
        if (data.session_renewed) {
          setSuccess(data.warning
            ? 'Password updated, but other sessions could not be signed out.'
            : 'Password updated successfully')
        } else {
          setSuccess('Password updated. Please sign in again with your new password.')
          removeTokens()
          globalThis.dispatchEvent(new CustomEvent('auth:session-expired'))
        }
      } else {
        setError(data.error || 'Failed to update password')
      }
    } catch (err) {
      setError('Network error. Please check if the backend server is running.')
      console.error('Update password error:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmitNotifications = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')
    setSuccess('')

    try {
      const response = await apiFetch('/api/profile', {
        method: 'PUT',
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          street: formData.street,
          postalCode: formData.postalCode,
          city: formData.city,
          emailNotifications: formData.emailNotifications,
          slackNotifications: formData.slackNotifications,
          telegramNotifications: formData.telegramNotifications
        })
      })

      const data = await response.json()

      if (response.ok) {
        setSuccess('Notification settings updated successfully')
      } else {
        setError(data.error || 'Failed to update notification settings')
      }
    } catch (err) {
      setError('Network error. Please check if the backend server is running.')
      console.error('Update notification settings error:', err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="content-section">
      <div className="profile-header">
        <h2>User Profile</h2>
        <button className="btn-primary" onClick={onBack}>← Back</button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {success && (
        <div className="success-message">
          {success}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          Allgemein
        </button>
        <button
          className={`tab-button ${activeTab === 'password' ? 'active' : ''}`}
          onClick={() => setActiveTab('password')}
        >
          Passwort
        </button>
        <button
          className={`tab-button ${activeTab === 'notifications' ? 'active' : ''}`}
          onClick={() => setActiveTab('notifications')}
        >
          Benachrichtigung
        </button>
      </div>

      {/* General Tab */}
      {activeTab === 'general' && (
        <form className="profile-form" onSubmit={handleSubmitGeneral}>
          <div className="form-section">
            <h3>Personal Information</h3>

            <AddressFields formData={formData} onChange={handleChange} isLoading={isLoading} />

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                readOnly
                disabled
                className="readonly-field"
              />
              <small className="field-help">Email cannot be changed as it serves as your username</small>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn-save"
              disabled={isLoading}
            >
              {isLoading && <span className="loading-spinner"></span>}
              {isLoading ? 'Saving...' : 'Save Updates'}
            </button>
          </div>
        </form>
      )}

      {/* Password Tab */}
      {activeTab === 'password' && (
        <form className="profile-form" onSubmit={handleSubmitPassword}>
          <div className="form-section">
            <h3>Change Password</h3>
            <p className="section-description">
              Update your password to keep your account secure
            </p>

            <div className="form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                placeholder="Enter current password"
                disabled={isLoading}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  placeholder="Enter new password"
                  disabled={isLoading}
                  minLength="12"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmNewPassword">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmNewPassword"
                  name="confirmNewPassword"
                  value={formData.confirmNewPassword}
                  onChange={handleChange}
                  placeholder="Confirm new password"
                  disabled={isLoading}
                  minLength="12"
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn-save"
              disabled={isLoading}
            >
              {isLoading && <span className="loading-spinner"></span>}
              {isLoading ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </form>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <form className="profile-form" onSubmit={handleSubmitNotifications}>
          <div className="form-section">
            <h3>Notification Settings</h3>
            <p className="section-description">
              Choose how you want to receive system notifications
            </p>

            <div className="notification-options">
              <div className="notification-item">
                <label htmlFor="emailNotifications" className="checkbox-label">
                  <input
                    id="emailNotifications"
                    type="checkbox"
                    name="emailNotifications"
                    checked={formData.emailNotifications}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                  <div className="notification-content">
                    <span className="notification-title">E-Mail Benachrichtigungen</span>
                    {' '}
                    <span className="notification-description">
                      Erhalten Sie E-Mail-Benachrichtigungen über wichtige Ereignisse wie ablaufende Access Tokens
                    </span>
                  </div>
                </label>
              </div>

              <div className="notification-item disabled">
                <label htmlFor="slackNotifications" className="checkbox-label">
                  <input
                    id="slackNotifications"
                    type="checkbox"
                    name="slackNotifications"
                    checked={formData.slackNotifications}
                    onChange={handleChange}
                    disabled
                  />
                  <div className="notification-content">
                    <span className="notification-title">
                      Slack Benachrichtigungen{' '}
                      <span className="coming-soon-badge">Demnächst</span>
                    </span>
                    {' '}
                    <span className="notification-description">
                      Erhalten Sie Benachrichtigungen direkt in Ihrem Slack-Workspace (Funktion in Entwicklung)
                    </span>
                  </div>
                </label>
              </div>

              <div className="notification-item disabled">
                <label htmlFor="telegramNotifications" className="checkbox-label">
                  <input
                    id="telegramNotifications"
                    type="checkbox"
                    name="telegramNotifications"
                    checked={formData.telegramNotifications}
                    onChange={handleChange}
                    disabled
                  />
                  <div className="notification-content">
                    <span className="notification-title">
                      Telegram Benachrichtigungen{' '}
                      <span className="coming-soon-badge">Demnächst</span>
                    </span>
                    {' '}
                    <span className="notification-description">
                      Erhalten Sie Benachrichtigungen über Telegram Bot (Funktion in Entwicklung)
                    </span>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn-save"
              disabled={isLoading}
            >
              {isLoading && <span className="loading-spinner"></span>}
              {isLoading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
      )}

      <style>{`
        .tab-navigation {
          display: flex;
          gap: 8px;
          margin-bottom: 30px;
          border-bottom: 2px solid rgba(79, 70, 229, 0.2);
        }

        .tab-button {
          padding: 12px 24px;
          background: transparent;
          border: none;
          color: #9ca3af;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          border-bottom: 3px solid transparent;
          margin-bottom: -2px;
        }

        .tab-button:hover {
          color: #e5e7eb;
          background: rgba(79, 70, 229, 0.1);
        }

        .tab-button.active {
          color: #4f46e5;
          border-bottom-color: #4f46e5;
        }

        .notification-options {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .notification-item {
          background: rgba(45, 45, 45, 0.6);
          border: 1px solid rgba(79, 70, 229, 0.2);
          border-radius: 8px;
          padding: 20px;
          transition: all 0.2s;
        }

        .notification-item:hover {
          background: rgba(45, 45, 45, 0.8);
          border-color: rgba(79, 70, 229, 0.4);
        }

        .notification-item.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .notification-item.disabled:hover {
          background: rgba(45, 45, 45, 0.6);
          border-color: rgba(79, 70, 229, 0.2);
        }

        .checkbox-label {
          display: flex;
          align-items: flex-start;
          cursor: pointer;
          gap: 15px;
        }

        .notification-item.disabled .checkbox-label {
          cursor: not-allowed;
        }

        .checkbox-label input[type="checkbox"] {
          margin-top: 3px;
          width: 20px;
          height: 20px;
          cursor: pointer;
          accent-color: #4f46e5;
        }

        .notification-item.disabled input[type="checkbox"] {
          cursor: not-allowed;
        }

        .notification-content {
          display: flex;
          flex-direction: column;
          gap: 6px;
          flex: 1;
        }

        .notification-title {
          font-weight: 600;
          color: #e5e7eb;
          font-size: 15px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .notification-description {
          color: #9ca3af;
          font-size: 13px;
          line-height: 1.5;
        }

        .coming-soon-badge {
          display: inline-block;
          background: rgba(251, 191, 36, 0.15);
          color: #fbbf24;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      `}</style>
    </div>
  )
}

UserProfile.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    street: PropTypes.string,
    postal_code: PropTypes.string,
    city: PropTypes.string,
    email: PropTypes.string,
    email_notifications: PropTypes.bool,
    slack_notifications: PropTypes.bool,
    telegram_notifications: PropTypes.bool,
  }).isRequired,
  onBack: PropTypes.func.isRequired,
}

export default UserProfile
